import {
  AR_ASSET_FORMATS,
  AR_ASSET_MIME,
  type ArAssetFormat,
  ArAssetParkResponse,
  arAssetGatewayPath,
} from "@pufimi/shared";
import { apiBaseUrl, readErrorEnvelope } from "@/lib/api";

// AR delivery (ADR-0035 §3, issue #381) — the client-side glue between the AR
// **bake** (`arBake.ts`, #380) and the OS AR engines. The bake flattens the live
// scene into self-contained Blobs — a GLB (Android Scene Viewer / WebXR) and a
// USDZ (iOS Quick Look); `park` uploads those bytes to the ungated AR-asset park
// endpoint (#379) and returns SHORT gateway URLs (`/ar-assets/parked/<name>`) that
// #382 hands to `<model-viewer>` (`src` = GLB, `ios-src` = USDZ) and #383 folds into
// the desktop QR; the API 302s each to a freshly-signed URL on open. The two halves stay
// cleanly separable: the bake produces bytes, delivery ships them — this module
// knows nothing about three.js, and `arBake` knows nothing about the network.
//
// Why a real URL and NEVER a `blob:` — the entire reason this module exists: the
// OS AR engines (Quick Look, Scene Viewer) run OUT OF PROCESS and cannot read a
// `blob:` / object URL bound to this document — they crash on one (ADR-0035 §3,
// proven in spike #343). So the bytes MUST live at a real network address served
// with the correct MIME. `park` therefore uploads to the endpoint and returns the
// SHORT gateway URL built from the parked name (never a `blob:`; the ~1 KB signed URL
// stays server-side so the desktop QR is scannable, ADR-0035 §7); it never calls
// `URL.createObjectURL`, and it guards the built URL so a non-fetchable one fails
// loudly here instead of downstream at the AR tap.
//
// The endpoint is a session-exempt capability plane (ADR-0035 §7): the upload is a
// plain, credential-free PUT of raw bytes to `/ar-assets/{format}`, and the server
// stamps the stored object's MIME from the `{format}` path — the client never gets
// to pick it. The unguessable server-minted key is the whole access model.

// The baked bytes to deliver, keyed by format. BOTH are optional so a caller can
// park only the format the target platform needs (iOS → `usdz`, Android → `glb`) —
// an allowed optimization (ADR-0035 §3): baking + uploading a single format is half
// the work when the visitor's platform is already known.
export type ArBakedBlobs = { [F in ArAssetFormat]?: Blob };

// The fetchable delivery URLs, one per format that was uploaded. Shaped as
// `{ glbUrl?, usdzUrl? }` so #382 can drop them straight onto `<model-viewer>`
// (`src={glbUrl}`, `ios-src={usdzUrl}`); a format that was not supplied is absent.
export type ArParkedUrls = { [F in ArAssetFormat as `${F}Url`]?: string };

// The delivery invariant, made executable (ADR-0035 §3): a parked URL MUST be a
// real, network-fetchable address the out-of-process OS AR engine can GET. The
// endpoint only ever returns a signed `https` URL, so this is defense-in-depth —
// it refuses a `blob:` / `data:` / object URL loudly rather than let one reach
// `<model-viewer>` (where the AR engine would crash). `http` is allowed so the
// localstack-signed dev URL still works; production URLs are `https` from the
// signer. This is the executable form of "park never delivers a blob: URL".
function assertFetchableArUrl(url: string): string {
  let protocol: string | undefined;
  try {
    protocol = new URL(url).protocol;
  } catch {
    protocol = undefined;
  }
  if (protocol !== "https:" && protocol !== "http:") {
    throw new Error(
      `arDelivery: refusing a non-fetchable delivery URL (${url || "<empty>"}) — the OS AR engine reads http(s) only, never a blob:/object URL (ADR-0035 §3)`,
    );
  }
  return url;
}

// PUT one baked Blob's raw bytes to the park endpoint and return the signed GET
// URL. Errors propagate as the shared typed `ApiError` (non-2xx: empty body →
// `file_too_small`, oversize → `file_too_large`, otherwise `internal_error`) or as
// the underlying `TypeError` on a network failure — so #382 can catch any of them
// and show a retry toast.
async function parkOne(format: ArAssetFormat, bytes: Blob): Promise<string> {
  const res = await fetch(`${apiBaseUrl}/ar-assets/${format}`, {
    method: "PUT",
    // The format's canonical MIME (SSOT `AR_ASSET_MIME`). The server re-stamps the
    // stored object's Content-Type from the `{format}` path, so this only makes the
    // request self-describing; it matches the type the bake already set on the Blob.
    headers: { "content-type": AR_ASSET_MIME[format] },
    // Raw octet body — the endpoint reads `arrayBuffer()` and stores it verbatim
    // (branch A: no server-side conversion, ADR-0035 §4).
    body: bytes,
    // Ungated plane (ADR-0035 §7): send NO session cookie. `same-origin` (the plain
    // -fetch default, NOT the api facade's `include`) also avoids the credentialed
    // -CORS handshake on a cross-origin PUT — the unguessable key is the security.
    credentials: "same-origin",
  });
  if (!res.ok) throw await readErrorEnvelope(res);
  const { name } = ArAssetParkResponse.parse(await res.json());
  // Build the SHORT gateway URL the OS AR engine / desktop QR uses — NEVER the ~1 KB
  // signed URL (which overflows a scannable QR, ADR-0035 §7). Absolute: resolved
  // against the cross-origin API base the PUT just used (falling back to this page's
  // origin), so it works both in-page AND when a phone opens the desktop QR off
  // -device; the API 302s it to a freshly-signed URL on each open. `assertFetchableArUrl`
  // keeps the blob:-never invariant executable — the URL is now always our own http(s)
  // origin by construction, so a `blob:` can never reach `<model-viewer>`.
  const gatewayUrl = new URL(arAssetGatewayPath(name), apiBaseUrl || window.location.origin);
  return assertFetchableArUrl(gatewayUrl.toString());
}

// Park the baked AR bytes and return their fetchable delivery URLs (ADR-0035 §3).
// Uploads every format present in `blobs` (one PUT each, concurrently) — pass only
// the platform-needed format to skip the other. Rejects if ANY upload fails, so a
// caller gets either every requested URL or a single error to retry (#382); a
// partially-parked object self-expires under the `ar/` short-TTL lifecycle rule,
// so no cleanup is owed.
export async function park(blobs: ArBakedBlobs): Promise<ArParkedUrls> {
  const pending = AR_ASSET_FORMATS.flatMap((format) => {
    const bytes = blobs[format];
    return bytes ? [{ format, bytes }] : [];
  });

  const parked = await Promise.all(
    pending.map(async ({ format, bytes }) => ({ format, url: await parkOne(format, bytes) })),
  );

  const urls: ArParkedUrls = {};
  for (const { format, url } of parked) {
    urls[`${format}Url`] = url;
  }
  return urls;
}
